/**
 * Karr Modules — the activation-layer registry (mod-1, DARK).
 *
 * Single source of truth that classifies every sidebar surface into a module.
 * See `docs/MODULES.md` for the build contract; the hard guardrails (Sec. 8):
 *   1. Activation is NEVER a security boundary — RLS/RBAC/entitlements are the
 *      only authorization layers. Never make a server decision on module state.
 *   2. Compliance NEVER silenced — the rules engine / Risk Ledger / Living
 *      Checklist key on FACTS, never on module activation. Compliance-tier
 *      modules are auto-resolved from the profile and are not user-deactivatable.
 *   3. No data deletion — deactivation hides surfaces only.
 *   4. Legacy orgs are pixel-identical until they explicitly interact
 *      (`enabled_modules === undefined` ⇒ today's nav, incl. service-nature
 *      goods hiding — reproduced here by absence semantics).
 *   5. No new visibility mechanisms — new gating is a registry entry, not code.
 *
 * This step ships the registry + the pure resolver ONLY. Nothing renders it yet
 * (mod-2 wires nav/route gating), so there is zero rendered diff.
 *
 * Three tiers:
 *   - **core**        always on; the accounting spine. Not a `ModuleDef`, not in
 *                     the gallery, never gated. Listed as `CORE_NAV_ITEM_IDS`.
 *   - **compliance**  auto-resolved from the org profile (predicate); read-only,
 *                     never user-deactivatable (guardrail 2).
 *   - **operational** user-activated per org (Owner/Admin).
 *
 * Config stays React-free (icons are lucide-react string names, the
 * `experienceLevel.ts` pattern) so it can be imported anywhere, incl. tests.
 *
 * @since mod-1 (2026-07-12)
 */
import type { BusinessNature } from './businessNature';
import { IN_COMPANY_ORG_TYPES, NONPROFIT_ORG_TYPES } from './countryConfig';
import type { SellerKind, SellFacts } from '@/shared/profile/sellProfile';
// Not from sellProfile: that carries the whole catalogue into the first load.
import { modulesForKinds } from '@/shared/profile/sellKindModules';

/* ─── Types ───────────────────────────────────────────────────────────────── */

export type ModuleTier = 'core' | 'compliance' | 'operational';

/**
 * The classification of a single nav leaf id: a module id, or `'core'` for the
 * always-on spine. `moduleOfNavItem`/`moduleOfPath` return this.
 */
export type ModuleClass = string; // module id | 'core'

/**
 * Profile facts the resolver reads — the same family the Living Checklist uses
 * (`src/shared/compliance/checklist.ts`). Only these four facts gate modules.
 */
export interface ModuleProfile {
    /** 'service' | 'trading' | 'manufacturing' | 'mixed' (orgs.business_nature). */
    businessNature?: BusinessNature | string | null;
    /** e.g. 'in_pvt_ltd', 'in_trust' (orgs.org_type). */
    orgType?: string | null;
    /** GST regime: 'regular' | 'composition' | 'unregistered' | 'exempt' (mig 00216). */
    taxRegime?: string | null;
    /** ISO code ('IN') or legacy full name ('India'). Absent ⇒ India (India-first default). */
    country?: string | null;
}

export interface ModuleDef {
    id: string;
    /** Never `'core'` — core surfaces are not `ModuleDef`s (they are always on). */
    tier: Exclude<ModuleTier, 'core'>;
    label: string;
    description: string;
    /** lucide-react icon name (string keeps this config React-free). */
    iconName: string;
    /** Sidebar leaf ids this module owns — the source of truth for nav gating. */
    navItemIds: string[];
    /** Route base paths this module owns (derived from navItemIds + `extraRoutePaths`). */
    routePaths: string[];
    /** Extra owned routes not present as nav leaves (sub-pages). */
    extraRoutePaths?: string[];
    /** Operational modules this one needs — requires-closure turns them on too. */
    requires?: string[];
    /**
     * Plan-entitlement key (`src/shared/lib/entitlements.ts` FEATURE_LABELS).
     * Orthogonal to activation (Sec. 4). Missing/undefined ⇒ always allowed
     * (today's behavior), so mod-1…4 need no plan changes.
     */
    entitlementKey?: string;
    /**
     * Business natures that seed this module ON at onboarding (mod-4).
     * `'all'` seeds it for EVERY org, whatever its nature (and even when the
     * nature is missing) — for a module every business eventually needs.
     */
    defaultFor?: BusinessNature[] | 'all';
    /**
     * verticals §3.0: off for a legacy org too. A legacy org (no explicit
     * list) gets every other operational module on; this one only when the
     * org switches it on, so a new vertical never appears in a business that
     * did not ask for it.
     */
    optIn?: boolean;
    /**
     * Table for the "you already use this — N records" chip (mod-3). RLS-scoped
     * count head-query, read-only. Exact names are re-confirmed against the live
     * schema in mod-3; only the high-confidence ones are populated here.
     */
    usageProbe?: string;
    /** Compliance tier only: predicate over the profile ("is this on?"). */
    predicate?: (p: ModuleProfile) => boolean;
    /** Compliance tier only: the human "why this is on" reason (mod-3 cards). */
    reason?: string;
}

/* ─── Core (always on — the accounting spine) ─────────────────────────────────
 *
 * Not modules: never toggled, never gated, never in the gallery. Kept as a flat
 * id set. Plan gating still applies WITHIN core via existing entitlements
 * (estimates, recurring, payment_links, report_builder, multi_currency, ai,
 * bank_feed, api) — that is an orthogonal axis (Sec. 4), untouched here.
 */
export const CORE_NAV_ITEM_IDS: ReadonlySet<string> = new Set<string>([
    // Home & setup
    'dashboard', 'my-businesses', 'contacts', 'items',
    // Books
    'accounts', 'opening-balances', 'journals', 'dimensions', 'fixed-expenses',
    // Capital & assets
    'fixed-assets', 'loans',
    // Buy (spine)
    'purchases', 'purchase-orders', 'debit-notes', 'recurring-bills', 'expenses',
    // Sell (spine)
    // A proforma is NOT a nav leaf: it is a kind of document raised from inside
    // Invoices, so there is no `proforma` id here and no route of its own. Both
    // it and estimates are core spine, which is what makes them free on every
    // plan rather than an activatable module somebody has to buy.
    'sales', 'estimates', 'credit-notes', 'recurring-invoices', 'payment-links',
    // Money movement
    'money-in', 'money-out', 'pay-week', 'transfers',
    'bank-statements', 'bank-reconciliation', 'bank-feeds', 'receivables',
    // Reports & close
    // 'audit' is CORE, never an activatable module. The engagement it shows is
    // a statutory relationship, not a feature a client subscribes to: an org
    // must not be able to switch off the screen its own auditor works from.
    // (The desk used to be a second leaf, 'audit-desk'; it is a chapter of
    // Audit now.)
    'reports', 'accruals', 'audit', 'my-audits', 'period-locks', 'report-builder',
    'multi-currency', 'books-health',
    // fr-a1 — CORE, never an activatable module. A notice arrives whether or
    // not the org switched a module on, and Sec. 8's guardrail is that
    // activation must never silence compliance: hiding this behind a toggle
    // would let an org opt out of being told a statutory window is closing.
    'notices',
    // sigreg · CORE, and it stopped being part of the ROC module on 19 Aug 2026.
    // The screen is entity-aware: a company keeps the Companies Act registers, and
    // every other entity sees a Register of Owners built on `equity_holdings`.
    // Both keep the Signatures tab, and a signature belongs to a person on a
    // register, so gating this screen behind a company-only module hid the only
    // place a firm's partners could be asked for one.
    'statutory-registers',
    // Tools & system
    'documents', 'receipt-scanner', 'tasks', 'ai-tools',
    // market — the shelf itself is always reachable; what it sells is gated by plan.
    'marketplace',
    'collaboration', 'audit-trail', 'integrations', 'import',
    'developers', 'partner',
]);

/* ─── Compliance predicates (fact-driven; the Living Checklist family) ─────── */

/** India-first: an explicit non-India country turns India cockpits off; unset ⇒ India. */
function isIndiaProfile(country?: string | null): boolean {
    const c = (country ?? '').trim().toLowerCase();
    return c === '' || c === 'in' || c === 'india';
}

/** GST applies for a registered regime; 'unregistered'/'exempt' turn it off. Absent ⇒ 'regular'. */
function gstApplies(taxRegime?: string | null): boolean {
    const r = (taxRegime ?? 'regular').trim().toLowerCase();
    return r === 'regular' || r === 'composition';
}

/* ─── Operational modules (user-activated) ────────────────────────────────── */

const OPERATIONAL_DEFS: Omit<ModuleDef, 'routePaths'>[] = [
    {
        id: 'inventory', tier: 'operational',
        label: 'Inventory & Warehousing',
        description: 'Stock, warehouses, goods receipts and delivery challans.',
        iconName: 'Warehouse',
        navItemIds: ['inventory', 'goods-received', 'delivery-challans'],
        entitlementKey: 'inventory',
        // stockhome-1 (3 Oct 2026): on for every business. Founder: "keep
        // inventory module open for all clients by default". The page is quiet
        // until there is a tracked item (the home's Stock card and its rows
        // appear only for a stock nature or a business with one), so an
        // advocate gets a nav entry and nothing else. Still deactivatable.
        defaultFor: 'all',
        usageProbe: 'stock_movements',
    },
    {
        id: 'pos', tier: 'operational',
        label: 'Point of Sale (counter)',
        description: 'Counter billing for retail and quick service.',
        iconName: 'ShoppingCart',
        navItemIds: ['pos'],
        entitlementKey: 'pos', // new key seeded in mod-5 (missing ⇒ allowed until then)
        defaultFor: ['trading'],
        usageProbe: 'pos_sales',
    },
    {
        id: 'restaurant', tier: 'operational',
        label: 'Restaurant',
        description: 'Floor, tables, KOT/KDS and food cost. Zomato and Swiggy are booked under Sales channels.',
        iconName: 'Utensils',
        // kp-5: the delivery apps folded into Sales channels (/ecommerce), where their statements are booked.
        navItemIds: ['restaurant', 'kds', 'food-cost'],
        entitlementKey: 'restaurant', // new key seeded in mod-5
        usageProbe: 'pos_open_orders',
    },
    {
        id: 'manufacturing', tier: 'operational',
        label: 'Manufacturing',
        description: 'Bills of materials, work orders and production.',
        iconName: 'Factory',
        navItemIds: ['manufacturing'],
        requires: ['inventory'],
        entitlementKey: 'manufacturing',
        defaultFor: ['manufacturing'],
        usageProbe: 'work_orders',
    },
    {
        id: 'quality', tier: 'operational',
        label: 'Quality (QMS)',
        description: 'Inspections and quality management.',
        iconName: 'BadgeCheck',
        navItemIds: ['quality'],
        entitlementKey: 'qms',
        usageProbe: 'quality_inspections',
    },
    {
        id: 'orders', tier: 'operational',
        label: 'Sales Orders & RFQ',
        description: 'Sales orders and requests for quotes.',
        iconName: 'PackageCheck',
        navItemIds: ['sales-orders', 'rfq'],
        entitlementKey: 'orders', // SKU rail (mod-5) — inert until a plan lists it
        defaultFor: ['trading', 'manufacturing', 'mixed'],
        usageProbe: 'sales_orders',
    },
    {
        id: 'payroll', tier: 'operational',
        label: 'Payroll & HR',
        description: 'Salaries, statutory payroll, leave and claims.',
        iconName: 'Briefcase',
        // `payroll-filing` lives in the Comply section of the nav but is gated
        // here, because the returns only exist if somebody runs payroll — an
        // org with no staff should not be shown a provident-fund deadline.
        navItemIds: ['payroll', 'hr', 'payroll-filing'],
    },
    {
        id: 'projects', tier: 'operational',
        label: 'Projects & Time',
        description: 'Project accounting and time tracking.',
        iconName: 'FolderKanban',
        navItemIds: ['projects', 'time-tracking'],
        entitlementKey: 'projects',
        defaultFor: ['service', 'mixed'],
        usageProbe: 'projects',
    },
    {
        id: 'ecommerce', tier: 'operational',
        label: 'Selling platforms',
        description: 'Online store orders and channels.',
        iconName: 'Store',
        navItemIds: ['ecommerce'],
        requires: ['inventory'],
        entitlementKey: 'ecommerce', // SKU rail (mod-5) — inert until a plan lists it
    },
    {
        id: 'subscriptions', tier: 'operational',
        label: 'Subscription Billing',
        description: 'Recurring subscription plans and billing.',
        iconName: 'Repeat',
        navItemIds: ['subscriptions'],
        entitlementKey: 'subscriptions', // SKU rail (mod-5) — inert until a plan lists it
        usageProbe: 'customer_subscriptions',
    },
    {
        id: 'crm', tier: 'operational',
        label: 'CRM',
        description: 'Leads, deals and pipeline.',
        iconName: 'Target',
        navItemIds: ['crm'],
        entitlementKey: 'crm', // SKU rail (mod-5) — inert until a plan lists it
        defaultFor: ['service'],
    },
    {
        id: 'helpdesk', tier: 'operational',
        label: 'Helpdesk',
        description: 'Support tickets and customer requests.',
        iconName: 'LifeBuoy',
        navItemIds: ['helpdesk'],
        entitlementKey: 'helpdesk', // SKU rail (mod-5) — inert until a plan lists it
    },
    {
        id: 'maintenance', tier: 'operational',
        label: 'Asset Maintenance',
        description: 'Maintenance schedules and jobs for assets.',
        iconName: 'Wrench',
        navItemIds: ['maintenance'],
        entitlementKey: 'maintenance', // SKU rail (mod-5) — inert until a plan lists it
    },
    {
        id: 'budgeting', tier: 'operational',
        label: 'Budgeting & Forecasts',
        description: 'Budgets and financial forecasts.',
        iconName: 'Target',
        navItemIds: ['budgeting'],
        entitlementKey: 'budgeting',
    },
    {
        id: 'approvals', tier: 'operational',
        label: 'Approval Workflows',
        description: 'Multi-step approval rules for documents.',
        iconName: 'ShieldCheck',
        navItemIds: ['approvals'],
        entitlementKey: 'approvals',
    },
    {
        id: 'consolidation', tier: 'operational',
        label: 'Multi-entity Consolidation',
        description: 'Group consolidation across entities.',
        iconName: 'Building2',
        navItemIds: ['consolidation'],
        entitlementKey: 'consolidation',
    },
    {
        id: 'portal', tier: 'operational',
        label: 'Customer / Vendor Portal',
        description: 'External self-service access for customers and vendors.',
        iconName: 'UserCircle',
        navItemIds: ['portal'],
        entitlementKey: 'client_portal',
    },
    {
        id: 'practice', tier: 'operational',
        label: 'Practice Suite',
        description: 'Clients, CRM and client requests (CA / consultant).',
        iconName: 'Briefcase',
        navItemIds: ['portfolio', 'practice-crm', 'requests', 'my-work'],
        entitlementKey: 'practice_dashboard',
    },
    /*
     * Karr Health (docs/KARR_HMS_SPEC.md section 4): one health product, one
     * patient-visit spine, departments an org switches on. Reception is the
     * spine every department stands on. A lab that hires a doctor switches on
     * Clinic and nothing moves. The lab keeps its old id so an org that had
     * today's lab keeps it. Department desks live under /hms (lazy chunk).
     * Prices are set on the price card when each department ships (16.3).
     */
    {
        id: 'hms', tier: 'operational',
        label: 'Reception',
        description: 'Karr Health: register patients, open visits and run the token queue. Free with any health department.',
        iconName: 'Heart',
        navItemIds: ['hms'],
        // hf-6: the hospital's pages live under /health; the old /hms forwards there.
        extraRoutePaths: ['/health'],
    },
    {
        id: 'lab', tier: 'operational',
        label: 'Lab',
        description: 'Karr Health: samples, results, reports and lab machines, for a lab or collection centre.',
        iconName: 'FlaskConical',
        navItemIds: ['lab'],
        extraRoutePaths: ['/health/lab'],
        requires: ['hms'],
        entitlementKey: 'lab',
    },
    {
        id: 'hms-clinic', tier: 'operational',
        label: 'Clinic',
        description: 'Karr Health: appointments, the consult pad, prescriptions and orders to the lab and scans.',
        iconName: 'Stethoscope',
        navItemIds: [],
        extraRoutePaths: ['/health/clinic'],
        requires: ['hms'],
    },
    {
        id: 'hms-pharmacy', tier: 'operational',
        label: 'Pharmacy',
        description: 'Karr Health: a prescription becomes a pick list at the till, with the Schedule H, H1 and X registers.',
        iconName: 'Pill',
        navItemIds: [],
        extraRoutePaths: ['/health/pharmacy'],
        requires: ['hms', 'inventory', 'pos'],
    },
    {
        id: 'hms-radiology', tier: 'operational',
        label: 'Radiology',
        description: 'Karr Health: X-ray, ultrasound and scan worklist, reports and the PC-PNDT Form F register.',
        iconName: 'ScanLine',
        navItemIds: [],
        extraRoutePaths: ['/health/radiology'],
        requires: ['hms'],
    },
    {
        // hd-8 (docs/KARR_HEALTH_DIRECT_SPEC.md §4): Cardiology on the study desk Radiology shares.
        id: 'hms-cardiology', tier: 'operational',
        label: 'Cardiology',
        description: 'Karr Health: ECG, Echo, TMT and Holter worklist, reports and the machine maps.',
        iconName: 'HeartPulse',
        navItemIds: [],
        extraRoutePaths: ['/health/cardiology'],
        requires: ['hms'],
    },
    {
        id: 'hms-inpatient', tier: 'operational',
        label: 'Beds and wards',
        description: 'Karr Health: admissions, the bed board, nursing, deposits and the discharge bill.',
        iconName: 'BedDouble',
        navItemIds: [],
        extraRoutePaths: ['/health/inpatient'],
        requires: ['hms', 'hms-clinic'],
    },
    {
        id: 'hms-insurance', tier: 'operational',
        label: 'Insurance and TPA',
        description: 'Karr Health: payers, pre-authorisation and claims as receivables in your books.',
        iconName: 'ShieldCheck',
        navItemIds: [],
        extraRoutePaths: ['/health/insurance'],
        requires: ['hms'],
    },
    /*
     * Karr Build (docs/KARR_BUILD_SPEC.md): the contractor's, the builder's and
     * the developer's projects. A Build project is Karr's own project (the job
     * every posting already carries), with the site's terms beside it. Desks
     * live under /build in Karr's shell (lazy chunk, build-1). Off until an org
     * turns it on: no defaultFor.
     */
    {
        id: 'build', tier: 'operational',
        label: 'Build',
        description: 'Karr Build: for contractors, builders and developers. The BOQ, measurements from the engineer\'s phone, running bills with retention, subcontractors, site stores, musters, petty cash, buyers\' demands, the RERA account and each site\'s cost to complete.',
        iconName: 'Building2',
        navItemIds: ['build'],
        usageProbe: 'build_projects',
    },
    /*
     * Karr Travel (docs/KARR_TRAVEL_SPEC.md section 7): one trip spine in eight
     * stages. Travel alone is the whole product for one person with a phone;
     * the others add to it, and each needs it. Desks live under /travel (lazy
     * chunk, travel-3). Prices are set on the price card (spec 16.1).
     */
    {
        id: 'travel', tier: 'operational',
        label: 'Travel',
        description: 'Karr Travel: every trip from the first ask to the last supplier paid, in eight stages, with the books done as it goes.',
        iconName: 'Plane',
        // travel-3: the /travel shell and its desks (All trips, Sales, Operations, Accounts);
        // travel-18: fixed departures.
        navItemIds: ['travel', 'travel-departures'],
    },
    {
        id: 'travel-ops', tier: 'operational',
        label: 'Travel operations',
        description: 'Karr Travel: the hotel desk, check-ins and vouchers, planned against actual cost, and the catalogue for a team.',
        iconName: 'ClipboardList',
        navItemIds: ['travel-hotels'],
        // The hotel desk (travel-8). The Operations desk itself is part of Travel.
        extraRoutePaths: ['/travel/hotels'],
        requires: ['travel'],
    },
    {
        id: 'travel-partners', tier: 'operational',
        label: 'Travel partners',
        description: 'Karr Travel: sell to other agents at a partner price, their own page, statements and commission.',
        iconName: 'Handshake',
        navItemIds: ['travel-agents'],
        extraRoutePaths: ['/travel/partners'],
        requires: ['travel'],
    },
    {
        id: 'travel-corporate', tier: 'operational',
        label: 'Corporate travel',
        description: 'Karr Travel: company accounts, traveller lists, a travel policy with approvals, and GST invoices to the employer.',
        iconName: 'Building2',
        navItemIds: ['travel-companies'],
        extraRoutePaths: ['/travel/corporate'],
        requires: ['travel'],
    },
    /*
     * Karr Me (docs/KARR_ME_SPEC.md): the person's own year, ready to sign on
     * 1 April. me-1 the person and the /me desk; me-2..me-9 the year's income,
     * gains, homes and proofs, the computation, advance tax, the ITR-1 and ITR-2
     * file with the CA's review and the person's sign, and the family. Off for
     * every org until it is switched on.
     */
    {
        id: 'me', tier: 'operational',
        label: 'Karr Me',
        description: 'Karr Me: your own year, salary, interest, shares and rent read as they happen, and the return ready to sign on 1 April. Free for an employee whose employer runs payroll on Karr.',
        iconName: 'UserCircle',
        navItemIds: ['me'],
        optIn: true,
    },
    /*
     * Karr Fleet (docs/KARR_FLEET_SPEC.md, fleet-1): the trip and the vehicle on
     * Karr's own rows. A vehicle is a fixed asset and a cost centre, a driver is
     * a payroll employee. Off until a business switches it on.
     */
    {
        id: 'fleet', tier: 'operational',
        label: 'Fleet',
        description: 'Karr Fleet: for transporters and fleet owners. Trips from the driver\'s phone, the LR and e-way bill, freight bills under the GTA option, FASTag and fuel on the trip, market trucks, papers due and each truck\'s P&L.',
        iconName: 'Truck',
        navItemIds: ['fleet'],
        usageProbe: 'fleet_vehicles',
        optIn: true,
    },
    /*
     * Karr Mandi (docs/KARR_MANDI_SPEC.md, mandi-1): an arhtiya's or a trader's
     * shop in a mandi. A farmer is a vendor contact, a buyer a customer contact,
     * a crop an item by the quintal; the lot at the gate becomes the patti, the
     * bill and the payment. Off until a shop switches it on.
     */
    {
        id: 'mandi', tier: 'operational',
        label: 'Mandi',
        description: 'Karr Mandi: for a commission agent (arhtiya) or a trader in a mandi. The gate, the auction, the patti and the bill, the farmer paid on UPI, crop advances, buyers, stock and the committee\'s monthly return, in English, Hindi, Punjabi or Urdu. ₹4,999 a shop a year, on top of your plan.',
        iconName: 'Wheat',
        navItemIds: ['mandi'],
        usageProbe: 'mandi_lots',
        optIn: true,
    },
    /*
     * Karr Stay (docs/KARR_STAY_SPEC.md, stay-1): hotels, guest houses,
     * homestays and PGs. A room type is an item sold by the night; the night
     * audit will be Karr's day-run. Off until a business switches it on.
     */
    {
        id: 'stay', tier: 'operational',
        label: 'Stay',
        description: 'Karr Stay: for hotels, guest houses, homestays and PGs. The front desk and the tape chart, the folio, a night audit that posts itself, checkout as one invoice at each night\'s GST slab, OTAs and their statements, your own booking page, housekeeping, Form C, the city ledger and PG rent.',
        iconName: 'BedDouble',
        navItemIds: ['stay'],
        usageProbe: 'stay_rooms',
        optIn: true,
    },
    /*
     * Karr Campus (docs/KARR_SCHOOL_SPEC.md, campus-1): schools, colleges,
     * coaching institutes and hostels. A student is a contact on the roll of a
     * class; a fee demand will be a Karr invoice. Off until a school switches it on.
     */
    {
        id: 'campus', tier: 'operational',
        label: 'Campus',
        description: 'Karr Campus: for schools, colleges, coaching institutes and hostels. Fees as invoices with the parent\'s own page, admissions, the class year with its certificates, buses and hostel, staff and the trust\'s filings.',
        iconName: 'GraduationCap',
        navItemIds: ['campus'],
        usageProbe: 'campus_students',
        optIn: true,
    },
    /*
     * Karr Jewel (docs/KARR_JEWEL_SPEC.md, jewel-1): jewellers, bullion
     * wholesalers and manufacturers. A purity is an item kept by the gram,
     * priced from the morning's fine rate. Off until a business switches it on.
     */
    {
        id: 'jewel', tier: 'operational',
        label: 'Jewel',
        description: 'Karr Jewel: for jewellers, bullion and chain wholesalers and jewellery makers. The morning rate board for gold, silver and platinum by purity, with the metal in stock at today\'s rate.',
        iconName: 'Gem',
        navItemIds: ['jewel'],
        usageProbe: 'jewel_metals',
        optIn: true,
    },
    /*
     * Karr Studio (docs/KARR_STUDIO_SPEC.md, studio-1): salons, barbers, spas,
     * gyms, yoga, pilates and dance studios, one module with its words chosen
     * on setup. A service is an item with a duration and the chair, room or
     * floor it needs. Off until a business switches it on.
     */
    {
        id: 'studio', tier: 'operational',
        label: 'Studio',
        description: 'Karr Studio: for salons, barbers, spas, gyms, yoga and dance studios. The day by chair or room, services with their time, and memberships that earn night by night, coming desk by desk.',
        iconName: 'Scissors',
        navItemIds: ['studio', 'studio-classes', 'studio-clients', 'studio-till', 'studio-staff', 'studio-morning', 'studio-page'],
        usageProbe: 'studio_resources',
        optIn: true,
    },
    /*
     * Karr Service (docs/KARR_SERVICE_SPEC.md, service-1): the AC and RO
     * dealer, the lift company, the IT firm that keeps offices running. A
     * customer's machine by serial and the contract that covers it; job cards,
     * spares from the van and warranty claims come next. Off until switched on.
     */
    {
        id: 'service', tier: 'operational',
        label: 'Service',
        description: 'Karr Service: for AMC and field service firms, such as AC and RO dealers, lift companies and IT service firms. Machines by serial with their own page, contracts that bill and earn themselves, job cards on the technician\'s phone with the SLA clock, spares from the van, warranty claims and commission.',
        iconName: 'Toolbox',
        navItemIds: ['service'],
        usageProbe: 'svc_machines',
        optIn: true,
    },
    /*
     * Karr Practice (docs/KARR_PRACTICE_SPEC.md): Karr Office for a law firm,
     * an architect, an engineer, a CS or a consultant, in the trade's own
     * words. Office's own key is `practice` (above, since mod-1), so this one
     * is `practice-matters`. Off for everyone until an org switches it on, and
     * a CA's Office never changes with it (founder, 23 Oct 2026).
     */
    {
        id: 'practice-matters', tier: 'operational',
        label: 'Karr Practice',
        description: 'Matters with their dates, time and client money, for a law firm, architect, engineer, CS or consultant.',
        iconName: 'Briefcase',
        // practice-door (25 Oct 2026): its own sidebar row, so a client's books
        // and a plain business find it too; Office's strip was its only door.
        navItemIds: ['practice-matters'],
        extraRoutePaths: ['/practice/matters'],
        requires: ['practice'],
        optIn: true,
    },
    {
        id: 'projections', tier: 'operational',
        label: 'Projections & Bank Finance',
        description: 'Bank-ready projected financial statements from your live books.',
        iconName: 'Landmark',
        navItemIds: ['projections'],
        entitlementKey: 'projections', // SKU rail — inert until a plan lists it
        usageProbe: 'financial_projections',
        // Every business meets a bank sooner or later, and the one moment you
        // need a projected balance sheet you need it TODAY. So this one is on
        // for everybody rather than something to go hunting for. Still
        // deactivatable in the gallery like any operational module.
        defaultFor: 'all',
    },
];

/* ─── Compliance modules (auto-resolved; read-only) ───────────────────────── */

const COMPLIANCE_DEFS: Omit<ModuleDef, 'routePaths'>[] = [
    {
        id: 'compliance-core', tier: 'compliance',
        label: 'Compliance Intelligence',
        description: 'Risk Ledger and the Living Compliance Checklist.',
        iconName: 'ClipboardCheck',
        navItemIds: ['compliance'],
        predicate: () => true,
        reason: 'Always on — the Risk Ledger and Living Checklist are the heart of Karr.',
    },
    {
        id: 'gst', tier: 'compliance',
        label: 'GST',
        description: 'GST filing cockpit, returns and live GST/TDS.',
        iconName: 'Landmark',
        // indiacockpit (4 Oct 2026): the cockpits talk to India's GST portal, so they
        // are India's only. Without the country check a Singapore business, whose
        // regime reads 'regular' by default, was shown "GST Filing Cockpit" and
        // "GST & TDS (Live)".
        navItemIds: ['gst-filing', 'gst-tds'],
        predicate: (p) => isIndiaProfile(p.country) && gstApplies(p.taxRegime),
        reason: 'On because your GST regime is registered (regular or composition).',
    },
    {
        id: 'tax-reports', tier: 'compliance',
        label: 'Tax reports',
        description: "The year's tax summary and the filing calendar, in your country's own tax.",
        iconName: 'ShieldCheck',
        // Split out of 'gst' (indiacockpit): the summary and the calendar read the
        // country's own setup, so they serve every country. In India they stay
        // where they were, with a registered GST regime.
        navItemIds: ['compliance-reports'],
        predicate: (p) => !isIndiaProfile(p.country) || gstApplies(p.taxRegime),
        reason: 'On because your business charges tax on its bills.',
    },
    {
        id: 'itr', tier: 'compliance',
        label: 'Income Tax',
        description: 'ITR filing cockpit, TDS/TCS statements and advance tax.',
        iconName: 'FileText',
        navItemIds: ['itr-filing', 'advance-tax', 'tds-filing'],
        predicate: (p) => isIndiaProfile(p.country),
        reason: 'On because every Indian business files income tax.',
    },
    {
        id: 'softex', tier: 'compliance',
        label: 'Software Exports',
        description: 'SOFTEX/EDF declarations to RBI for software and IT service exports.',
        iconName: 'Globe',
        navItemIds: ['softex'],
        predicate: (p) => isIndiaProfile(p.country),
        reason: 'On because any Indian business that invoices a client abroad for software or IT services has to declare it to RBI.',
    },
    {
        id: 'roc', tier: 'compliance',
        label: 'ROC Compliance',
        description: 'The Companies Act year as a map: filings, meetings and papers.',
        iconName: 'MapPinned',
        // `statutory-registers` left this module in sigreg and became CORE. The
        // screen is entity-aware now (a non-company sees a Register of Owners),
        // so it must not sit behind a company-only module. CORE rather than
        // unclassified: unclassified fails open at runtime, but `modules.test.ts`
        // requires every sidebar leaf to be classified, and it is right to.
        navItemIds: ['roc'],
        predicate: (p) => IN_COMPANY_ORG_TYPES.has(p.orgType ?? ''),
        reason: 'On because you are a company under the Companies Act 2013.',
    },
    {
        id: 'ngo', tier: 'compliance',
        label: 'NGO Reporting',
        description: 'Donation receipts, the section 354 approval register, Form 113, and non-profit statutory reports.',
        iconName: 'Heart',
        navItemIds: ['donations', 'ngo-reports'],
        predicate: (p) => NONPROFIT_ORG_TYPES.includes(p.orgType ?? ''),
        reason: 'On because you are a non-profit (trust, society or Section 8).',
    },
];

/* ─── Assembled registry (routePaths computed once) ───────────────────────── */

function withRoutePaths(def: Omit<ModuleDef, 'routePaths'>): ModuleDef {
    return {
        ...def,
        routePaths: [
            ...def.navItemIds.map((id) => `/${id}`),
            ...(def.extraRoutePaths ?? []),
        ],
    };
}

export const OPERATIONAL_MODULES: ModuleDef[] = OPERATIONAL_DEFS.map(withRoutePaths);
export const COMPLIANCE_MODULES: ModuleDef[] = COMPLIANCE_DEFS.map(withRoutePaths);

/** Every module (operational + compliance). Core is intentionally absent. */
export const MODULES: ModuleDef[] = [...OPERATIONAL_MODULES, ...COMPLIANCE_MODULES];

/* ─── Lookups (built once — pure, boot cost ≈ 0) ──────────────────────────── */

const OPERATIONAL_BY_ID = new Map(OPERATIONAL_MODULES.map((m) => [m.id, m]));
const MODULE_BY_ID = new Map(MODULES.map((m) => [m.id, m]));
const OPERATIONAL_ID_SET: ReadonlySet<string> = new Set(OPERATIONAL_MODULES.map((m) => m.id));

/** nav leaf id → module id | 'core'. */
const NAV_ITEM_TO_MODULE = new Map<string, ModuleClass>();
for (const id of CORE_NAV_ITEM_IDS) NAV_ITEM_TO_MODULE.set(id, 'core');
for (const m of MODULES) for (const nav of m.navItemIds) NAV_ITEM_TO_MODULE.set(nav, m.id);

/** [routePath, module id | 'core'] — longest-prefix match in `moduleOfPath`. */
const PATH_TO_MODULE: [string, ModuleClass][] = [
    ...[...CORE_NAV_ITEM_IDS].map((id) => [`/${id}`, 'core'] as [string, ModuleClass]),
    ...MODULES.flatMap((m) => m.routePaths.map((rp) => [rp, m.id] as [string, ModuleClass])),
];

/**
 * Operational modules hidden from a LEGACY pure-service org (the resolver's
 * Sec. 3/Sec. 4 formula). This IS the old `GOODS_ONLY_NAV_ITEM_IDS` set (mig 00111),
 * subsumed into the activation layer and retired from `businessNature.ts` in
 * mod-6 — every leaf of these modules was a goods-only surface, so hiding at
 * module granularity reproduces the old per-leaf nature hiding exactly. Nature
 * now SEEDS modules (Sec. 5); it no longer gates nav.
 *
 * `inventory` left this set in stockhome-1 (3 Oct 2026): Inventory is on for
 * every business, a legacy service org included.
 */
export const GOODS_HIDDEN_OPERATIONAL_IDS: ReadonlySet<string> = new Set([
    'pos', 'ecommerce', 'manufacturing', 'quality',
]);

/* ─── Public API ──────────────────────────────────────────────────────────── */

/** The module id (or `'core'`) that owns a sidebar leaf id, or `undefined` if unclassified. */
export function moduleOfNavItem(navItemId: string): ModuleClass | undefined {
    return NAV_ITEM_TO_MODULE.get(navItemId);
}

/** The module id (or `'core'`) that owns a route path, by longest-prefix match. */
export function moduleOfPath(path: string): ModuleClass | undefined {
    const clean = (path || '').split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
    let best: ModuleClass | undefined;
    let bestLen = -1;
    for (const [rp, id] of PATH_TO_MODULE) {
        if ((clean === rp || clean.startsWith(rp + '/')) && rp.length > bestLen) {
            best = id;
            bestLen = rp.length;
        }
    }
    return best;
}

/** Look up a `ModuleDef` by id (operational or compliance). */
export function getModuleDef(id: string): ModuleDef | undefined {
    return MODULE_BY_ID.get(id);
}

/**
 * Close a set of operational ids under `requires` (activating manufacturing
 * pulls inventory, etc.). Non-operational / unknown ids are dropped.
 */
export function requiresClosure(ids: readonly string[]): Set<string> {
    const out = new Set<string>();
    const visit = (id: string) => {
        if (out.has(id) || !OPERATIONAL_ID_SET.has(id)) return;
        out.add(id);
        OPERATIONAL_BY_ID.get(id)?.requires?.forEach(visit);
    };
    ids.forEach(visit);
    return out;
}

/**
 * The active NON-core module ids for an org. Core is always on and is NOT in the
 * returned set (see `isNavItemVisibleByModule`).
 *
 * @param profile  the org's profile facts (drives the compliance tier)
 * @param enabled  `orgs.org_config.enabled_modules`. `undefined`/`null` ⇒ LEGACY
 *                 org: every operational module on, minus the goods-only ones for
 *                 a pure-service business — i.e. today's nav, exactly (guardrail 4).
 */
export function resolveModules(
    profile: ModuleProfile,
    enabled?: readonly string[] | null,
): Set<string> {
    const active = new Set<string>();

    // Compliance tier — auto-resolved from facts, never user-controlled (guardrail 2).
    for (const m of COMPLIANCE_MODULES) {
        if (m.predicate?.(profile)) active.add(m.id);
    }

    // Operational tier.
    if (enabled == null) {
        // Legacy org — no explicit list. Everything on, minus goods-only modules
        // for a pure-service business. Reproduces today's visibility exactly.
        const hideGoods = String(profile.businessNature ?? '') === 'service';
        for (const m of OPERATIONAL_MODULES) {
            if (hideGoods && GOODS_HIDDEN_OPERATIONAL_IDS.has(m.id)) continue;
            if (m.optIn) continue;
            active.add(m.id);
        }
    } else {
        // Explicit org — exactly the chosen set, closed under `requires`.
        for (const id of requiresClosure(enabled)) active.add(id);
    }

    return active;
}

/**
 * Whether a nav leaf should be visible given the active module set. Core surfaces
 * are always visible; an unclassified id fails OPEN (the totality test guarantees
 * no real nav id is unclassified, so this is only a never-hit safety net —
 * activation must never accidentally hide a surface). Compose with the existing
 * experience-level / RBAC / plan checks in the sidebar (mod-2).
 */
export function isNavItemVisibleByModule(
    navItemId: string,
    activeModuleIds: ReadonlySet<string>,
): boolean {
    const cls = moduleOfNavItem(navItemId);
    if (cls === undefined || cls === 'core') return true;
    return activeModuleIds.has(cls);
}

/**
 * The lean starter set of operational modules to seed a NEW org with, from its
 * business nature (mod-4 · docs/MODULES.md Sec. 5). Derived from each module's
 * `defaultFor` so the registry stays the single source:
 *   service → projects, crm · trading → pos, orders
 *   manufacturing → manufacturing, orders · mixed → orders, projects
 * plus `defaultFor: 'all'` modules (inventory, projections), which every org gets.
 * The compliance tier resolves itself from org_type/regime — nothing to seed.
 */
export function seedModulesFor(
    nature: BusinessNature | string | null | undefined,
    kinds: readonly SellerKind[] = [],
    facts?: SellFacts | null,
): string[] {
    const n = String(nature ?? '');
    // howsell: what the business ticked on "Are you any of these?" adds the
    // modules its trade needs (used goods: inventory; a cafe: restaurant, POS).
    // hms-2: a health business's places add its HMS departments.
    const forKinds = requiresClosure(modulesForKinds(kinds, facts));
    return OPERATIONAL_MODULES.filter((m) => (
        m.defaultFor === 'all' || (m.defaultFor ?? []).includes(n as BusinessNature) || forKinds.has(m.id)
    )).map((m) => m.id);
}

/**
 * Split the operational catalogue into the gallery's three groups (mod-3 Sec. 6):
 *   - active     currently on
 *   - available  off, and the plan allows it (or it has no entitlement key —
 *                missing key ⇒ allowed, today's behavior, Sec. 4)
 *   - locked     off, and gated behind a higher plan
 *
 * Pure: the caller supplies `isActive` (from resolveModules) and `isEntitled`
 * (from the plan). Never a security decision — display grouping only (guardrail 1).
 */
export function partitionOperationalModules(
    isActive: (moduleId: string) => boolean,
    isEntitled: (def: ModuleDef) => boolean,
): { active: ModuleDef[]; available: ModuleDef[]; locked: ModuleDef[] } {
    const active: ModuleDef[] = [];
    const available: ModuleDef[] = [];
    const locked: ModuleDef[] = [];
    for (const m of OPERATIONAL_MODULES) {
        if (isActive(m.id)) active.push(m);
        else if (!m.entitlementKey || isEntitled(m)) available.push(m);
        else locked.push(m);
    }
    return { active, available, locked };
}
